import '@xterm/xterm/css/xterm.css'
import { useEffect } from 'react'
import { LuMaximize2, LuMinimize2, LuX, LuExternalLink, LuLink2, LuLink2Off } from 'react-icons/lu'
import { SERVICE_DOMAIN } from '../constants'
import IconCopyButton from './IconCopyButton'
import { useTheme } from '@mui/material/styles'
import { Link } from 'react-router-dom'
import Box from '@mui/material/Box'
import IconButton from '@mui/material/IconButton'
import Stack from '@mui/material/Stack'
import Tooltip from '@mui/material/Tooltip'
import Typography from '@mui/material/Typography'
import useMediaQuery from '@mui/material/useMediaQuery'
import DialogContent from '@mui/material/DialogContent'
import CircularProgress from '@mui/material/CircularProgress'
import IconMinimize from './IconMinimize'
import Logo from './Logo'
import useTerminalService from '../hooks/useTerminalService'
import { Z_INDEX } from '../constants'

type Props = {
  sessionId: string
  logo?: boolean
  isActive?: boolean
  standalone?: boolean
  onStatusChange?: (status: 'connecting' | 'connected' | 'disconnected') => void
  onClose: () => void
  onExpand?: () => void
  onReduce?: () => void
  onMinimize?: () => void
  onFocus?: () => void
}

const INACTIVE_OPACITY = 0.3

export default function Terminal({
  sessionId,
  logo,
  isActive,
  standalone,
  onStatusChange,
  onClose,
  onExpand,
  onReduce,
  onMinimize,
  onFocus,
}: Props) {
  const theme = useTheme()
  const isMobile = useMediaQuery(theme.breakpoints.down('md'))

  const { service, status, host, title, node, serviceName, containerRef, termRootRef, toggleConnection } =
    useTerminalService({ sessionId })

  useEffect(() => {
    onStatusChange?.(status)
  }, [onStatusChange, status])

  if (!serviceName) return null

  const newTabHref = serviceName ? `/terminal/${encodeURIComponent(serviceName)}` : '/terminal'
  const tooltipProps = {
    slotProps: {
      popper: {
        sx: {
          zIndex: Z_INDEX.TOOLTIP,
        },
      },
    },
  } as const

  return (
    <Stack
      ref={containerRef}
      sx={{
        height: '100%',
        minHeight: '300px',
        boxSizing: 'border-box'
      }}>
      {/* Header */}
      <DialogContent
        sx={{
          pb: 2,
          flexGrow: 0,
          flexShrink: 0,
          backdropFilter: theme => theme.shape.backdropBlur,
          opacity: isActive ? 1 : INACTIVE_OPACITY,
          '& .terminal-header-actions': {
            opacity: isActive ? 1 : 0,
            pointerEvents: isActive ? 'auto' : 'none',
            transition: theme => theme.transitions.create('opacity', { duration: theme.transitions.duration.shorter }),
          },
        }}
      >
        <Stack
          direction="row"
          sx={{
            gap: 1,
            alignItems: 'center',
            justifyContent: 'space-between'
          }}>
          <Stack
            direction="row"
            sx={{
              gap: 1,
              alignItems: 'center',
              minWidth: 0
            }}>
            <Stack
              direction="row"
              sx={{
                alignItems: 'center',
                gap: 2,
                minWidth: 0
              }}>
              {!isMobile && logo && (
                <Link to="/">
                  <Logo sx={{ margin: -3, marginTop: -3.5 }} />
                </Link>
              )}
              <Tooltip title={status === 'connected' ? 'Disconnect terminal' : 'Connect terminal'} {...tooltipProps}>
                <span>
                  <IconButton
                    size="small"
                    color={status === 'connected' ? 'primary' : 'secondary'}
                    onClick={toggleConnection}
                    disabled={status === 'connecting' || !host}
                  >
                    {status === 'connecting' ? (
                      <CircularProgress size={14} thickness={6} color="inherit" />
                    ) : status === 'connected' ? (
                      <LuLink2 size={16} />
                    ) : (
                      <LuLink2Off size={16} />
                    )}
                  </IconButton>
                </span>
              </Tooltip>
              <Typography
                variant="subtitle1"
                sx={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}
              >
                {title || 'Loading...'}
              </Typography>
              {node && (
                <Typography variant="caption" sx={{
                  color: 'contrast.medium'
                }}>
                  {node.name}
                </Typography>
              )}
            </Stack>
          </Stack>
          <Stack
            className="terminal-header-actions"
            direction="row"
            sx={{
              gap: 1,
              alignItems: 'center'
            }}>
            {!isMobile && standalone && (
              <Tooltip title="Copy link" {...tooltipProps}>
                <IconCopyButton
                  value={`https://${serviceName.toLowerCase()}.${SERVICE_DOMAIN}`}
                  iconColor="inherit"
                />
              </Tooltip>
            )}
            {!isMobile && !standalone && (
              <Tooltip title="Open in new tab" {...tooltipProps}>
                <IconButton
                  size="small"
                  component={Link}
                  to={newTabHref}
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  <LuExternalLink size={16} />
                </IconButton>
              </Tooltip>
            )}
            {!standalone && onReduce && !isMobile && (
              <Tooltip title="Close full screen" {...tooltipProps}>
                <IconButton size="small" onClick={onReduce}>
                  <LuMinimize2 size={16} />
                </IconButton>
              </Tooltip>
            )}
            {!standalone && onExpand && (
              <Tooltip title="Open full screen" {...tooltipProps}>
                <IconButton size="small" onClick={onExpand}>
                  <LuMaximize2 size={16} />
                </IconButton>
              </Tooltip>
            )}
            {!standalone && onMinimize && (
              <Tooltip title="Minimize" {...tooltipProps}>
                <IconButton
                  size="small"
                  onClick={onMinimize}
                  sx={{
                    alignSelf: 'flex-end',
                  }}
                >
                  <IconMinimize sx={{ fontSize: 16 }} />
                </IconButton>
              </Tooltip>
            )}
            {!standalone && (
              <Tooltip title="Close" {...tooltipProps}>
                <IconButton size="small" onClick={onClose}>
                  <LuX size={16} />
                </IconButton>
              </Tooltip>
            )}
          </Stack>
        </Stack>
      </DialogContent>
      {/* Terminal */}
      <Box
        className="terminal-content"
        sx={{
          flexGrow: 1,
          px: 2,
          py: 1,
          backgroundColor: theme.palette.background.default,
          cursor: 'text',
          overflow: 'hidden',
        }}
        onPointerDown={e => {
          e.stopPropagation()
          onFocus?.()
          service?.focus()
        }}
      >
        <Box ref={termRootRef} sx={{ height: '100%', width: '100%', opacity: isActive ? 1 : INACTIVE_OPACITY }} />
      </Box>
    </Stack>
  )
}
